import { useEffect, useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import {
  api, ErrorDeApi, type CedulaDeConciliacion, type Cliente, type TipoDeDiferenciaDeConciliacion,
} from '../servicios/api'
import { fechaContable } from '../fechas'
import { nombreDePeriodo, periodosDisponibles } from '../periodos'
import { PaqueteDeDeclaraciones } from '../componentes/PaqueteDeDeclaraciones'

const dinero = (n: number) =>
  n.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

const importe = (n: number | null) => (n === null ? '—' : dinero(n))

const ETIQUETAS: Record<TipoDeDiferenciaDeConciliacion, string> = {
  CfdiSinPoliza: 'CFDI sin póliza',
  IvaSinCfdi: 'IVA en póliza sin CFDI',
  IvaDistinto: 'IVA distinto entre CFDI y póliza',
  CfdiCanceladoConIvaRegistrado: 'CFDI cancelado con IVA en póliza',
  CobroSinComplementoDePago: 'Cobro sin complemento de pago',
}

const mensaje = (e: unknown, respaldo: string) => (e instanceof ErrorDeApi ? e.message : respaldo)

/**
 * Cédula de conciliación CFDI ↔ contabilidad (RF-1205). Contrasta los CFDI del mes contra las
 * pólizas vigentes y lista cada diferencia con lo que hay que hacer. Señala, no corrige: la
 * contabilidad la corrige el contador con una póliza de ajuste en un periodo abierto (RN-06).
 *
 * Una cédula sin diferencias solo dice algo si dice cuánto revisó: por eso el alcance va siempre
 * arriba, y un mes sin CFDI ni pólizas se dice como «nada que conciliar», no como «conciliado».
 */
export function ConciliacionCfdi() {
  const { id: clienteId } = useParams<{ id: string }>()
  const hoy = new Date()
  const periodos = useMemo(() => periodosDisponibles(hoy), [hoy.getFullYear(), hoy.getMonth()])
  const [anio, setAnio] = useState(periodos[1].anio)
  const [mes, setMes] = useState(periodos[1].mes)
  const [cliente, setCliente] = useState<Cliente>()
  const [cedula, setCedula] = useState<CedulaDeConciliacion>()
  const [error, setError] = useState('')
  const [errorAlExportar, setErrorAlExportar] = useState('')

  useEffect(() => {
    if (!clienteId) return
    api.obtenerCliente(clienteId).then(setCliente).catch(() => setCliente(undefined))
  }, [clienteId])

  useEffect(() => {
    if (!clienteId) return
    // Cambiar de periodo con una petición en vuelo: la respuesta tardía no pisa a la vigente.
    let vigente = true
    setCedula(undefined)
    setError('')
    setErrorAlExportar('')
    api.cedulaDeConciliacionCfdi(clienteId, anio, mes)
      .then((c) => vigente && setCedula(c))
      .catch((e) => vigente && setError(mensaje(e, 'No se pudo calcular la cédula de conciliación.')))
    return () => { vigente = false }
  }, [clienteId, anio, mes])

  async function exportar(formato: 'excel' | 'pdf') {
    if (!clienteId) return
    setErrorAlExportar('')
    try {
      await api.exportarConciliacionCfdi(clienteId, anio, mes, formato)
    } catch (e) {
      setErrorAlExportar(mensaje(e, 'No se pudo exportar la cédula.'))
    }
  }

  if (!clienteId) return <p>Cargando…</p>

  const sinActividad = cedula?.advertencias.some((a) => a.codigo === 'SIN_ACTIVIDAD') ?? false
  const porTipo = cedula
    ? (Object.keys(ETIQUETAS) as TipoDeDiferenciaDeConciliacion[])
        .map((tipo) => ({ tipo, cuantas: cedula.diferencias.filter((d) => d.tipo === tipo).length }))
        .filter((t) => t.cuantas > 0)
    : []

  return (
    <>
      <p className="sutil"><Link to={`/clientes/${clienteId}`}>← {cliente?.razonSocial ?? 'Expediente'}</Link></p>

      <header className="encabezado linea-base">
        <h1>Conciliación CFDI ↔ contabilidad</h1>
        <select
          className="compacta"
          style={{ width: 'auto' }}
          aria-label="Periodo"
          value={`${anio}-${mes}`}
          onChange={(e) => {
            const [a, m] = e.target.value.split('-')
            setAnio(Number(a))
            setMes(Number(m))
          }}
        >
          {periodos.map((p) => (
            <option key={`${p.anio}-${p.mes}`} value={`${p.anio}-${p.mes}`}>{nombreDePeriodo(p.anio, p.mes)}</option>
          ))}
        </select>
        {cliente && <span className="encabezado-sub mono">{cliente.rfc} · {cliente.razonSocial}</span>}
      </header>

      <PaqueteDeDeclaraciones clienteId={clienteId} anio={anio} mes={mes} />

      <p className="nota">
        Contrasta los CFDI emitidos en el mes contra las pólizas vigentes con fecha en el mes. Señala las diferencias, no las
        corrige: eso es tuyo, con una póliza de ajuste en un periodo abierto (RN-06).
      </p>

      {error && <p className="error" role="alert">{error}</p>}
      {errorAlExportar && <p className="error" role="alert">{errorAlExportar}</p>}
      {!cedula && !error && <p className="sutil">Calculando la cédula…</p>}

      {cedula && (
        <>
          <p className="nota">
            Revisado: {cedula.revisado.comprobantes} CFDI y {cedula.revisado.polizas} pólizas del periodo
            {cedula.revisado.descartados > 0 && ` (${cedula.revisado.descartados} CFDI descartados no se concilian)`}.
          </p>

          {cedula.advertencias.length > 0 && (
            <ul className="aviso" role="status" aria-label="Advertencias">
              {cedula.advertencias.map((a, i) => <li key={`${a.codigo}-${i}`}>{a.mensaje}</li>)}
            </ul>
          )}

          <section aria-labelledby="titulo-diferencias">
            <h2 id="titulo-diferencias">
              {cedula.diferencias.length === 0
                ? 'Diferencias'
                : `${cedula.diferencias.length} ${cedula.diferencias.length === 1 ? 'diferencia' : 'diferencias'}`}
            </h2>

            {cedula.diferencias.length === 0 ? (
              !sinActividad && <p className="vacio">Sin diferencias en lo revisado.</p>
            ) : (
              <>
                <p className="nota">{porTipo.map((t) => `${ETIQUETAS[t.tipo]}: ${t.cuantas}`).join(' · ')}</p>
                <div className="tabla-envoltura">
                  <table className="tabla">
                    <thead>
                      <tr>
                        <th scope="col">Diferencia</th>
                        <th scope="col">Fecha</th>
                        <th scope="col">Póliza</th>
                        <th scope="col">CFDI</th>
                        <th scope="col">Contraparte</th>
                        <th scope="col" className="num">IVA según CFDI</th>
                        <th scope="col" className="num">IVA según contabilidad</th>
                        <th scope="col">Qué hacer</th>
                      </tr>
                    </thead>
                    <tbody>
                      {cedula.diferencias.map((d, i) => (
                        <tr key={`${d.tipo}-${d.uuid ?? d.polizaId}-${i}`}>
                          <th scope="row">{ETIQUETAS[d.tipo]}</th>
                          <td>{fechaContable(d.fecha)}</td>
                          <td className="mono">{d.polizaFolio ?? '—'}</td>
                          <td className="mono" title={d.uuid ?? undefined}>{d.uuid ? `${d.uuid.slice(0, 8)}…` : '—'}</td>
                          <td>{d.contraparte ?? '—'}</td>
                          <td className="num">{importe(d.ivaSegunCfdi)}</td>
                          <td className="num">{importe(d.ivaSegunContabilidad)}</td>
                          <td className="sutil">{d.mensaje}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}

            <p className="acciones-de-fila">
              <button className="enlace" onClick={() => void exportar('excel')}>Exportar .xlsx</button>
              <button className="enlace" onClick={() => void exportar('pdf')}>Exportar PDF</button>
            </p>
          </section>

          <details className="nota">
            <summary>Lo que esta cédula no revisa</summary>
            <ul>
              {cedula.noRevisa.map((limite) => <li key={limite}>{limite}</li>)}
            </ul>
          </details>
        </>
      )}
    </>
  )
}
